import { useCallback, useRef, useState } from 'react'

/**
 * Undo for a message. Typing within a second of the last keystroke is one
 * step, so Cmd+Z takes back a phrase rather than a letter; a draft, a
 * rewrite or a template is always a step of its own, so the banner's Undo
 * returns exactly what was there before the Assistant wrote.
 *
 * A controlled textarea loses the browser's own undo the moment its value is
 * set from outside, which is why this exists at all. `useDraftHistory` knows
 * about proposals and is not reused.
 */

const COALESCE_MS = 1000
const LIMIT = 100

interface State<T> {
  past: T[]
  present: T
  future: T[]
  typing: boolean
}

export interface TextHistory<T> {
  value: T
  /** A keystroke. Runs of them merge into one step. */
  type: (next: T) => void
  /** A change made for the person: a draft, a rewrite, a template. */
  commit: (next: T) => void
  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean
}

export function useTextHistory<T>(initial: T | (() => T)): TextHistory<T> {
  const [state, setState] = useState<State<T>>(() => ({
    past: [],
    present: typeof initial === 'function' ? (initial as () => T)() : initial,
    future: [],
    typing: false,
  }))
  const lastTyped = useRef(0)

  const type = useCallback((next: T) => {
    const now = Date.now()
    const recent = now - lastTyped.current < COALESCE_MS
    lastTyped.current = now
    setState((current) =>
      current.typing && recent
        ? { ...current, present: next, future: [] }
        : { past: [...current.past, current.present].slice(-LIMIT), present: next, future: [], typing: true },
    )
  }, [])

  const commit = useCallback((next: T) => {
    lastTyped.current = 0
    setState((current) => ({
      past: [...current.past, current.present].slice(-LIMIT),
      present: next,
      future: [],
      typing: false,
    }))
  }, [])

  const undo = useCallback(() => {
    lastTyped.current = 0
    setState((current) => {
      const previous = current.past.at(-1)
      if (previous === undefined) return current
      return { past: current.past.slice(0, -1), present: previous, future: [current.present, ...current.future], typing: false }
    })
  }, [])

  const redo = useCallback(() => {
    lastTyped.current = 0
    setState((current) => {
      const [next, ...rest] = current.future
      if (next === undefined) return current
      return { past: [...current.past, current.present], present: next, future: rest, typing: false }
    })
  }, [])

  return {
    value: state.present,
    type,
    commit,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  }
}
